import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { mdiClose, mdiOpenInNew, mdiUpload } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Link } from '@tanstack/react-router'
import { formatCost } from '../../lib/capex-utils'
import { useUpdateWorkPackage } from '../../hooks/useCapexQueries'
import { useUploadDocument } from '../../hooks/useQueries'
import type { WorkPackage } from './types'
import { useCapexCurrency } from './CapexCurrencyContext'

interface QuoteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  pkg: WorkPackage | null
  org: string
}

export function QuoteDialog({ open, onOpenChange, pkg, org }: QuoteDialogProps) {
  const capexCurrency = useCapexCurrency()
  const { t } = useTranslation('capex')

  const midpoint = pkg ? ((pkg.cost_low ?? 0) + (pkg.cost_high ?? 0)) / 2 : 0
  const [amount, setAmount] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const updateMutation = useUpdateWorkPackage(org)
  const uploadMutation = useUploadDocument(org)

  // Pre-fill amount when dialog opens: existing quote takes priority over midpoint
  useEffect(() => {
    if (open && pkg) {
      if (pkg.quote_amount != null) {
        setAmount(String(pkg.quote_amount))
      } else if (midpoint > 0) {
        setAmount(String(Math.round(midpoint)))
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pkg])

  const resetAndClose = () => {
    setAmount('')
    setFile(null)
    setError('')
    setSaving(false)
    onOpenChange(false)
  }

  const handleSave = async () => {
    const val = Number(amount)
    if (!amount || isNaN(val) || val <= 0) {
      setError(t('create_package_dialog.error_cost_low_invalid'))
      return
    }
    if (!pkg) return

    setSaving(true)
    setError('')

    try {
      await updateMutation.mutateAsync({
        packageUid: pkg.uid!,
        body: { quote_amount: val } as any,
      })

      if (file) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('name', file.name)
        formData.append('link_entity_type', 'work_package')
        formData.append('link_entity_uid', pkg.uid!)
        await uploadMutation.mutateAsync(formData)
      }

      resetAndClose()
    } catch {
      setSaving(false)
    }
  }

  const handleOpenChange = (o: boolean) => {
    if (!o) resetAndClose()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png']
      if (!validTypes.includes(f.type)) {
        setError('File must be PDF, JPG, or PNG')
        return
      }
      setFile(f)
      setError('')
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const f = e.dataTransfer.files?.[0]
    if (f) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png']
      if (!validTypes.includes(f.type)) {
        setError('File must be PDF, JPG, or PNG')
        return
      }
      setFile(f)
      setError('')
    }
  }

  if (!pkg) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle>
            {pkg?.quote_amount != null ? t('quote.edit_dialog_title', 'Edit quote') : t('quote.dialog_title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Package context */}
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{pkg.label}</span>
            {(pkg.cost_low != null && pkg.cost_high != null) && (
              <p className="mt-1">
                {t('quote.estimate_range', {
                  low: formatCost(pkg.cost_low, capexCurrency),
                  high: formatCost(pkg.cost_high, capexCurrency),
                })}
              </p>
            )}
          </div>

          {/* Existing document link */}
          {(pkg as any).quote_document_uid && (
            <Link
              to="/$org/documents/$docUid"
              params={{ org, docUid: (pkg as any).quote_document_uid }}
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              target="_blank"
              data-testid="quote-document-link"
            >
              <AppIcon path={mdiOpenInNew} size="xs" />
              {t('quote.view_document', 'View current document')}
            </Link>
          )}

          {/* Amount input */}
          <div className="space-y-1.5">
            <Label htmlFor="quote-amount">{t('quote.amount_label')} (€)</Label>
            <Input
              id="quote-amount"
              type="number"
              min={0}
              step="any"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setError('') }}
              placeholder={t('quote.amount_placeholder')}
              data-testid="quote-amount-input"
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          {/* File drop zone */}
          <div className="space-y-1.5">
            <Label>{t('quote.file_label')}</Label>
            <div
              className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              data-testid="quote-file-dropzone"
            >
              {file ? (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <AppIcon path={mdiUpload} size="sm" />
                  <span className="text-foreground">{file.name}</span>
                  <span className="text-muted-foreground">
                    ({(file.size / 1024).toFixed(0)} KB)
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    onClick={(e) => { e.stopPropagation(); setFile(null) }}
                    data-testid="quote-file-remove"
                  >
                    <AppIcon path={mdiClose} size="xs" />
                  </Button>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  <AppIcon path={mdiUpload} size="lg" className="mx-auto mb-2" />
                  {t('quote.file_drop')}
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={resetAndClose}>
            {t('create_package_dialog.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !amount}
            data-testid="quote-save-btn"
          >
            {saving ? t('quote.saving') : t('quote.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
